import { useState } from "react";
import { toast } from "sonner";
import { Globe, Save } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { FixFailedButton } from "./FixFailedButton";

const Field = ({ label, children }) => (
  <label className="block"><span className="eyebrow">{label}</span><div className="mt-2">{children}</div></label>
);

export const ProductTab = ({ kit, reload }) => {
  const [p, setP] = useState(kit.product);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const blockers = kit.summary.blockers;

  const save = async (override = {}) => {
    setBusy(true);
    try {
      const { data } = await http.put(`/kits/${kit.id}/product`, { ...p, ...override, price: Number(p.price) });
      setP(data);
      toast.success(data.published ? "Published to catalogue" : "Product saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const feed = `${process.env.REACT_APP_BACKEND_URL}/api/public/products/${p.slug}`;

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8 rounded-xl border border-stone-200 bg-white p-8 space-y-6">
        <Field label="Product title"><Input data-testid="product-title-input" value={p.title} onChange={(e) => set("title", e.target.value)} className="h-11 bg-[#F7F5F0]" /></Field>
        <Field label="Subtitle"><Input data-testid="product-subtitle-input" value={p.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="h-11 bg-[#F7F5F0]" /></Field>
        <Field label="Sales description"><Textarea data-testid="product-description-input" value={p.description} onChange={(e) => set("description", e.target.value)} className="min-h-[160px] bg-[#F7F5F0]" /></Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Price"><Input data-testid="product-price-input" type="number" value={p.price} onChange={(e) => set("price", e.target.value)} className="h-11 bg-[#F7F5F0]" /></Field>
          <Field label="Currency"><Input data-testid="product-currency-input" value={p.currency} onChange={(e) => set("currency", e.target.value)} className="h-11 bg-[#F7F5F0]" /></Field>
          <Field label="URL slug"><Input data-testid="product-slug-input" value={p.slug} onChange={(e) => set("slug", e.target.value)} className="h-11 bg-[#F7F5F0] font-mono-ui text-xs" /></Field>
        </div>
        <Field label="Buy link on pbtravelangel.com"><Input data-testid="product-buy-url-input" placeholder="https://pbtravelangel.com/shop/…" value={p.buy_url} onChange={(e) => set("buy_url", e.target.value)} className="h-11 bg-[#F7F5F0]" />
          {!p.buy_url && <span data-testid="product-buy-url-auto" className="block text-[11px] text-stone-500 mt-1">Blank = automatic link from your pattern (Orders & Delivery page)</span>}</Field>
        <button data-testid="product-save-button" disabled={busy} onClick={() => save()} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-6 py-2.5 font-bold hover:bg-[#B8973A] transition-colors"><Save size={16} /> Save product</button>
      </div>
      <div className="lg:col-span-4 space-y-6">
        <div className="rounded-xl border border-stone-200 bg-white p-6">
          <div className="eyebrow">Publishing gate</div>
          <div className="flex items-center justify-between mt-4">
            <div>
              <div className="font-display text-xl text-[#2B2A26]">{p.published ? "Live in catalogue" : "Not published"}</div>
              <div data-testid="publish-gate-status" className={`text-xs mt-1 ${blockers ? "text-amber-400" : "text-emerald-400"}`}>{blockers ? `${blockers} item(s) need review` : "Gate clean — ready to sell"}</div>
            </div>
            <Switch data-testid="publish-toggle" checked={!!p.published} disabled={busy} onCheckedChange={(v) => save({ published: v })} />
          </div>
          {blockers > 0 && <p className="text-xs text-stone-500 mt-4">Fix & replace web-verifies every venue, swaps anything unverified for a verified alternative and corrects wrong facts. Kits can only go live once everything is verified.</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            <FixFailedButton kit={kit} count={blockers} reload={reload} testId="product-autofix" />
          </div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-6">
          <div className="eyebrow flex items-center gap-2"><Globe size={12} /> Feed for pbtravelangel.com</div>
          <p className="text-xs text-stone-500 mt-3">Your main site can read published products from this JSON endpoint:</p>
          <code data-testid="product-feed-url" className="block mt-2 text-[11px] font-mono-ui text-[#9A7B2C] break-all">{feed}</code>
          <p className="text-xs text-stone-500 mt-3">Upload the “Product bundle” (PDF + interactive app) as the downloadable files of the shop product.</p>
        </div>
      </div>
    </div>
  );
};
